import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import {
    FormControl,
    FormControlLabel,
    makeStyles,
    Radio,
    RadioGroup,
    TextareaAutosize,
    Theme,
    Typography
} from '@material-ui/core';

import { Page } from '../../components/Page';
import { BackAppBar } from '../../components/BackAppBar';
import { PageBody } from '../../components/PageBody';
import { FormGroup } from '../../components/FormGroup';
import { TextField } from '../../components/TextField';
import { Button } from '../../components/Button';
import { api } from '../../api';
import { useLoading } from '../../units/loading';
import { useAlert } from '../../units/alert';
import { StatusFalseError } from '../../api/error/StatusFalseError';
import { Satisfaction, SatisfactionRequestData } from '../../api/satisfaction';
import { useQuery } from '../../units/useQuery';

type FeedbackPageProps = {};

SatisfactionPage.defaultProps = {};

const useStyles = makeStyles((theme: Theme) => ({
    radioGroup: {
        '& .MuiFormControlLabel-root': {
            margin: theme.spacing(0, 1),
        },
    },
}));

function useFormData() {
    const [formData, setFormData] = useState<SatisfactionRequestData>({
        name: '',
        satisfaction: '5',
        feedback: '',
    });

    return {
        formData,
        setFormData,
    };
}

function SatisfactionPage(props: FeedbackPageProps): React.ReactElement {
    const classes = useStyles();
    const history = useHistory();
    const { setIsLoading } = useLoading();
    const alert = useAlert();
    const { formData, setFormData } = useFormData();
    const query = useQuery();
    const [feedbackIs, setFeedbackIs] = useState(false);

    useEffect(() => {
        if (query.get('name')) {
            setFormData({
                ...formData,
                name: query.get('name') || '',
            });

            setIsLoading(true);
            api.satisfactionCheck({
                name: query.get('name') || '',
            }).then((resData) => {
                setFeedbackIs(resData.feedback_is === 'TRUE');
                setIsLoading(false);
                if (resData.feedback_is === 'TRUE') {
                    alert.show({
                        type: 'success',
                        message: '已填寫滿意度，感謝您的回饋!',
                    });
                }
            }).catch(async (e) => {
                setIsLoading(false);
                if (e instanceof StatusFalseError) {
                    const errorData = await e.response.json();
                    alert.show({
                        type: 'error',
                        title: '錯誤',
                        message: errorData.msg,
                    });
                }
            });
        }
    }, [query?.get('name')]);

    function handleSubmitClick() {
        setIsLoading(true);
        api.satisfaction({
            ...formData,
        })
            .then(() => {
                setFeedbackIs(true);
                setIsLoading(false);
                alert.show({
                    type: 'success',
                    title: '操作成功',
                    message: '已成功送出意見反饋',
                });
            })
            .catch(async (e) => {
                setIsLoading(false);
                if (e instanceof StatusFalseError) {
                    const errorData = await e.response.json();
                    alert.show({
                        type: 'error',
                        title: '錯誤',
                        message: errorData.msg,
                    });
                }
            });
    }

    function canSubmit() {
        return formData.name
            && formData.satisfaction
            && formData.feedback
            && !feedbackIs;
    }

    return (
        <Page>
            <BackAppBar title="滿意度回饋" />
            <PageBody>
                <FormGroup title="報價單號">
                    <TextField label="報價單號" value={formData.name} disabled />
                </FormGroup>
                <FormGroup title="對於本次服務的滿意度">
                    <RadioGroup
                        className={classes.radioGroup}
                        value={formData.satisfaction}
                        onChange={(e, v) => setFormData({
                            ...formData,
                            satisfaction: v as Satisfaction,
                        })}
                    >
                        <FormControlLabel disabled={feedbackIs} value="5" control={<Radio color="primary" />} label="五顆星(★★★★★★)" />
                        <FormControlLabel disabled={feedbackIs} value="4" control={<Radio color="primary" />} label="四顆星(★★★★)" />
                        <FormControlLabel disabled={feedbackIs} value="3" control={<Radio color="primary" />} label="三顆星(★★★)" />
                        <FormControlLabel disabled={feedbackIs} value="2" control={<Radio color="primary" />} label="二顆星(★★)" />
                        <FormControlLabel disabled={feedbackIs} value="1" control={<Radio color="primary" />} label="一顆星(★)" />
                    </RadioGroup>
                </FormGroup>
                <FormGroup title="回饋內容">
                    <TextField
                        minRows={6}
                        multiline
                        placeholder="請填寫問題內容(限填150字)"
                        value={formData.feedback}
                        onChange={(e) => setFormData({
                            ...formData,
                            feedback: e.target.value,
                        })}
                        disabled={feedbackIs}
                    />
                </FormGroup>
                <FormGroup>
                    <Button onClick={handleSubmitClick} disabled={!canSubmit()}>送出</Button>
                </FormGroup>
            </PageBody>
        </Page>
    );
}

export { SatisfactionPage };
